import React, { useEffect, useMemo, useState } from 'react';
import NavigationDropdown from './NavigationDropdown';
import MobileMenuDrawer from './MobileMenuDrawer';

const Header = ({ siteUrl = '/', siteLogo = null, siteTitle = 'ARENES', navigation = [] }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState('PT');
  const [translations, setTranslations] = useState({});
  const [currentTheme, setCurrentTheme] = useState('dark');

  const t = (key, fallback = key) => translations[key] || fallback;

  const normalizeLabel = (value = '') =>
    String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const normalizeNavigation = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
      return items;
    }

    const byLabel = new Set(items.map((item) => normalizeLabel(item.label || item.slug || '')));

    const contactsItems = items.filter((item) => {
      const key = normalizeLabel(item.label || item.slug || '');
      return key === 'contacts' || key === 'contactos' || key === 'contact';
    });

    const worldChildren = items.filter((item) => {
      const key = normalizeLabel(item.label || item.slug || '');
      return key === 'africa' || key === 'asia' || key === 'america';
    });

    let normalized = items
      .filter((item) => {
        const key = normalizeLabel(item.label || item.slug || '');
        return key !== 'contacts' && key !== 'contactos' && key !== 'contact' && key !== 'africa' && key !== 'asia' && key !== 'america';
      })
      .map((item) => {
        const key = normalizeLabel(item.label || item.slug || '');

        if ((key === 'resources' || key === 'recursos') && contactsItems.length > 0) {
          return {
            ...item,
            children: [
              ...(Array.isArray(item.children) ? item.children : []),
              ...contactsItems.map((child) => ({ label: 'Contacts', url: child.url || '/contacts/' }))
            ]
          };
        }

        if ((key === 'world' || key === 'terra' || key === 'earth') && worldChildren.length > 0) {
          return {
            ...item,
            children: [
              ...(Array.isArray(item.children) ? item.children : []),
              ...worldChildren.map((child) => ({
                label: normalizeLabel(child.label) === 'america' ? 'America' : normalizeLabel(child.label) === 'asia' ? 'Asia' : 'Africa',
                url: child.url
              }))
            ]
          };
        }

        return item;
      });

    if (worldChildren.length > 0 && !byLabel.has('world') && !byLabel.has('terra') && !byLabel.has('earth')) {
      normalized = [
        ...normalized,
        {
          label: 'World',
          url: '/tag/earth/',
          children: [
            { label: 'Africa', url: worldChildren.find((item) => normalizeLabel(item.label) === 'africa')?.url || '/tag/africa/' },
            { label: 'Asia', url: worldChildren.find((item) => normalizeLabel(item.label) === 'asia')?.url || '/tag/asia/' },
            { label: 'America', url: worldChildren.find((item) => normalizeLabel(item.label) === 'america')?.url || '/tag/america/' }
          ]
        }
      ];
    }

    if (contactsItems.length > 0 && !normalized.some((item) => ['resources', 'recursos'].includes(normalizeLabel(item.label || item.slug || '')))) {
      normalized = [
        ...normalized,
        {
          label: 'Resources',
          url: '/resources/',
          children: [{ label: 'Contacts', url: contactsItems[0].url || '/contacts/' }]
        }
      ];
    }

    return normalized;
  };

  const navItems = useMemo(() => {
    const fallbackNavigation = [
      { label: 'Sobre', url: '/about/' },
      { label: 'Espetaculos', url: '/shows/' },
      { label: 'Ouvir', url: '/podcasts/' },
      { label: 'World', url: '/tag/earth/', children: [
        { label: 'Africa', url: '/tag/africa/' },
        { label: 'Asia', url: '/tag/asia/' },
        { label: 'America', url: '/tag/america/' }
      ] },
      { label: 'Recursos', url: '/resources/', children: [
        { label: 'Contactos', url: '/contacts/' }
      ] }
    ];

    if (Array.isArray(navigation) && navigation.length > 0) {
      return normalizeNavigation(navigation);
    }

    const domLinks = Array.from(document.querySelectorAll('.gh-head-menu .nav a[href]'))
      .map((link) => ({
        label: link.textContent.trim().replace(/[\+\-]$/, '').trim(),
        url: link.getAttribute('href')
      }))
      .filter((item) => item.label && item.url)
      .filter((item, index, arr) => arr.findIndex((candidate) => candidate.url === item.url) === index);

    return domLinks.length > 0 ? normalizeNavigation(domLinks) : fallbackNavigation;
  }, [navigation]);

  useEffect(() => {
    const savedLang = localStorage.getItem('ghost-lang') || 'pt';
    const savedTheme = localStorage.getItem('ghost-theme') || document.documentElement.getAttribute('data-theme') || 'dark';

    setCurrentLang(savedLang.toUpperCase());
    setCurrentTheme(savedTheme);
    document.documentElement.lang = savedLang;
    document.documentElement.setAttribute('data-theme', savedTheme);

    const loadTranslations = async (lang) => {
      try {
        const url = window.ghostTranslationUrls?.[lang];
        if (!url) return;
        const response = await fetch(url);
        if (!response.ok) return;
        setTranslations(await response.json());
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };

    loadTranslations(savedLang);

    const onScroll = () => setScrolled(window.scrollY > 30);
    const onLanguageChange = (event) => {
      const next = event?.detail?.lang;
      if (!next) return;
      setCurrentLang(String(next).toUpperCase());
      loadTranslations(next);
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('languageChange', onLanguageChange);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('languageChange', onLanguageChange);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('is-command-open');
      document.body.style.overflow = 'hidden';
      return;
    }

    document.body.classList.remove('is-command-open');
    document.body.style.overflow = '';
  }, [mobileMenuOpen]);

  const handleLanguageChange = async (lang) => {
    const langLower = String(lang).toLowerCase();
    setCurrentLang(String(lang).toUpperCase());
    setLanguageMenuOpen(false);
    localStorage.setItem('ghost-lang', langLower);

    try {
      const url = window.ghostTranslationUrls?.[langLower];
      if (url) {
        const response = await fetch(url);
        if (response.ok) {
          setTranslations(await response.json());
        }
      }
    } catch (error) {
      console.error('Failed to load translations:', error);
    }

    document.documentElement.lang = langLower;
    window.dispatchEvent(new CustomEvent('languageChange', { detail: { lang: langLower } }));
  };

  const handleSearch = () => {
    const searchButton = document.querySelector('[data-ghost-search]');
    if (searchButton) {
      searchButton.dispatchEvent(new Event('click'));
    }
  };

  const handleToggleTheme = () => {
    const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setCurrentTheme(nextTheme);
    localStorage.setItem('ghost-theme', nextTheme);
    document.documentElement.setAttribute('data-theme', nextTheme);
    window.dispatchEvent(new CustomEvent('themeChange', { detail: { theme: nextTheme } }));
  };

  return (
    <>
      <style>{`
        .gh-head-react {
          position: sticky;
          top: 0;
          z-index: 999;
          background: rgba(255, 255, 255, 0.97);
          border-bottom: 1px solid rgba(15, 23, 42, 0.1);
          backdrop-filter: blur(12px) saturate(140%);
          height: 78px;
          transition: box-shadow 0.2s ease, background 0.2s ease;
        }

        .gh-head-react.scrolled {
          box-shadow: 0 4px 18px rgba(15, 23, 42, 0.08);
          background: rgba(255, 255, 255, 0.99);
        }

        .gh-head-inner-react {
          max-width: 1320px;
          margin: 0 auto;
          height: 100%;
          padding: 0 1.4rem;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 1.2rem;
        }

        .gh-head-logo-react {
          color: #0f172a;
          font-weight: 800;
          text-decoration: none;
          font-size: 1.05rem;
        }

        .gh-head-logo-react img {
          height: 56px;
          width: auto;
          object-fit: contain;
        }

        .gh-head-menu-react {
          display: flex;
          justify-content: center;
        }

        .gh-head-actions-react {
          display: flex;
          align-items: center;
          gap: 0.55rem;
        }

        .gh-icon-btn-react {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid rgba(15, 23, 42, 0.16);
          background: #f8fafc;
          color: #0f172a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.16s ease, background 0.16s ease;
        }

        .gh-icon-btn-react:hover {
          transform: translateY(-1px);
          background: #eef2f7;
        }

        .gh-icon-btn-react svg {
          width: 18px;
          height: 18px;
        }

        .gh-lang-btn-react {
          min-width: 48px;
          padding: 0 0.7rem;
          font-size: 0.8rem;
          font-weight: 800;
        }

        .gh-language-switcher-react {
          position: relative;
        }

        .gh-language-dropdown-react {
          position: absolute;
          right: 0;
          top: calc(100% + 8px);
          min-width: 90px;
          background: #fff;
          border: 1px solid rgba(15, 23, 42, 0.14);
          border-radius: 10px;
          padding: 0.35rem;
          box-shadow: 0 12px 22px rgba(15, 23, 42, 0.14);
          display: none;
          z-index: 1300;
        }

        .gh-language-dropdown-react.is-open {
          display: block;
        }

        .gh-language-dropdown-react button {
          width: 100%;
          text-align: left;
          border: 0;
          background: transparent;
          color: #0f172a;
          border-radius: 8px;
          padding: 0.5rem 0.6rem;
          font-weight: 700;
          cursor: pointer;
        }

        .gh-language-dropdown-react button:hover {
          background: #eef2f7;
        }

        .gh-mobile-menu-btn-react {
          display: none;
        }

        [data-theme="dark"] .gh-head-react {
          background: rgba(11, 18, 32, 0.95);
          border-bottom-color: rgba(255, 255, 255, 0.14);
        }

        [data-theme="dark"] .gh-head-react.scrolled {
          background: rgba(11, 18, 32, 0.98);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.32);
        }

        [data-theme="dark"] .gh-head-logo-react,
        [data-theme="dark"] .gh-icon-btn-react,
        [data-theme="dark"] .gh-language-dropdown-react button {
          color: #f8fafc !important;
        }

        [data-theme="dark"] .gh-icon-btn-react {
          background: rgba(30, 41, 59, 0.8);
          border-color: rgba(255, 255, 255, 0.18);
        }

        [data-theme="dark"] .gh-icon-btn-react:hover {
          background: rgba(51, 65, 85, 0.86);
        }

        [data-theme="dark"] .gh-language-dropdown-react {
          background: #111827;
          border-color: rgba(255, 255, 255, 0.16);
        }

        [data-theme="dark"] .gh-language-dropdown-react button:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        @media (max-width: 980px) {
          .gh-head-inner-react {
            grid-template-columns: auto 1fr;
          }

          .gh-head-menu-react {
            display: none;
          }

          .gh-mobile-menu-btn-react {
            display: inline-flex;
          }

          .gh-head-actions-react {
            justify-self: end;
          }
        }

        @media (max-width: 520px) {
          .gh-head-react {
            height: 68px;
          }

          .gh-head-logo-react img {
            height: 42px;
          }

          .gh-icon-btn-react {
            width: 36px;
            height: 36px;
          }

          .gh-lang-btn-react {
            min-width: 44px;
            padding: 0 0.5rem;
            font-size: 0.74rem;
          }
        }
      `}</style>

      <header className={`gh-head-react ${scrolled ? 'scrolled' : ''}`}>
        <div className="gh-head-inner-react">
          <a href={siteUrl} className="gh-head-logo-react">
            {siteLogo ? <img src={siteLogo} alt={siteTitle} width="260" height="56" /> : siteTitle}
          </a>

          <nav className="gh-head-menu-react" aria-label={t('Primary navigation', 'Primary navigation')}>
            <NavigationDropdown navigation={navItems} />
          </nav>

          <div className="gh-head-actions-react">
            <div className="gh-language-switcher-react">
              <button
                type="button"
                className="gh-icon-btn-react gh-lang-btn-react"
                onClick={() => setLanguageMenuOpen((prev) => !prev)}
                aria-label={t('Choose language', 'Choose language')}
              >
                {currentLang}
              </button>
              <div className={`gh-language-dropdown-react ${languageMenuOpen ? 'is-open' : ''}`}>
                <button type="button" onClick={() => handleLanguageChange('PT')}>PT</button>
                <button type="button" onClick={() => handleLanguageChange('EN')}>EN</button>
              </div>
            </div>

            <button
              type="button"
              className="gh-icon-btn-react"
              onClick={handleToggleTheme}
              aria-label={t('Toggle theme', 'Toggle theme')}
            >
              {currentTheme === 'dark' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2" />
                  <path d="M12 20v2" />
                  <path d="M4.93 4.93l1.41 1.41" />
                  <path d="M17.66 17.66l1.41 1.41" />
                  <path d="M2 12h2" />
                  <path d="M20 12h2" />
                  <path d="M4.93 19.07l1.41-1.41" />
                  <path d="M17.66 6.34l1.41-1.41" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3c-.14.62-.21 1.27-.21 1.93a8 8 0 0 0 8 8c.66 0 1.31-.07 2-.14z" />
                </svg>
              )}
            </button>

            <button
              type="button"
              className="gh-icon-btn-react"
              onClick={handleSearch}
              aria-label={t('Search', 'Search')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
            </button>

            <button
              type="button"
              className="gh-mobile-menu-btn gh-mobile-menu-btn-react gh-icon-btn-react"
              onClick={() => setMobileMenuOpen(true)}
              aria-label={t('Open menu', 'Open menu')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </header>

      <MobileMenuDrawer
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        navigation={navItems}
        t={t}
        currentLang={currentLang}
        onLanguageChange={handleLanguageChange}
        currentTheme={currentTheme}
        onToggleTheme={handleToggleTheme}
      />
    </>
  );
};

export default Header;
